import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { Reference, Task } from 'src/careflow/ph-core/fhir-client/resources';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import { useStandardLoadingBackdrop } from 'src/careflow/ph-core/ui/base/StandardLoadingBackdropProvider';
import { CustomModalBasicProps } from 'src/components/CustomModal';
import useCheckNTaskLinker from 'src/hooks/useCheckNTaskLinker';
import useRevisitTaskValidation from 'src/hooks/useRevisitTaskValidation';
import { INTERVIEW_ACTIVE_STATUSES } from './constants';
import { fhirClient } from 'src/App';
import { TaskWrapper, WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { getAssessmentFromTask, getAssessmentUrlWithUserId } from 'src/careflow/helpers/common';
import { mapFhirResourceReference } from 'src/careflow/ph-core/workflow/helpers/workflow';
import AssessmentFrame from 'src/careflow/care-coordination/patient/assessment/frame/AssessmentFrame';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { Button, Grid } from '@mui/material';
import Iconify from 'src/components/Iconify';

interface Props extends CustomModalBasicProps {
  patient: WrappedPatient | null;
  open: boolean;
  displayStartRelatedAssessmentFirst?: boolean;
  handlerSave: (data: any) => void;
  onClose: () => void;
  checkListData: any;
  basedOn?: Reference;
}

export default function AssessmentFromChecklistData({
  patient,
  open,
  displayStartRelatedAssessmentFirst,
  onClose,
  handlerSave,
  checkListData,
  basedOn,
}: Props) {
  const translate = useTranslations(`assessmentDialog`);
  const authUser = useAuth().getCurrentUser();

  const isRevisitTask = checkListData?.code?.includes('REVISIT_UNANSWERED_NEEDS');
  const revisitValidation = useRevisitTaskValidation(
    isRevisitTask ? patient?.id || null : null,
    checkListData
  );

  const activityDefinitionId: string | null = useMemo(() => {
    if (!checkListData) {
      return null;
    }

    try {
      const metadata = JSON.parse(checkListData.metadata);
      const { activityDefinitionRef } = metadata.ui.data;
      const activityDefinitionId = activityDefinitionRef.split('/')[1];

      if (typeof activityDefinitionId === 'string') {
        return activityDefinitionId;
      }
    } catch (err) {
      return null;
    }

    return null;
  }, [checkListData]);

  const metadata = useMemo(() => {
    if (!checkListData) {
      return null;
    }

    try {
      const metadata = JSON.parse(checkListData.metadata);
      const {
        errorMessage = null,
        createTask,
        filterLast = false,
        allowMany = false,
      } = metadata?.ui?.data ?? {};

      return {
        errorMessage:
          typeof errorMessage === 'string'
            ? errorMessage
            : translate('notReadyTryAgainLaterMessage'),
        createTask,
        filterLast,
        allowMany,
      };
    } catch (err) {
      return {
        errorMessage: translate('notReadyTryAgainLaterMessage'),
        createTask: undefined,
        filterLast: false,
        allowMany: false,
      };
    }
  }, [checkListData]);

  const patientId = useMemo(() => patient?.id || '', [patient]);

  const standardLoadingBackdrop = useStandardLoadingBackdrop();
  const { enqueueSnackbar } = useSnackbar();

  const taskLinker = useCheckNTaskLinker({ patientId, activityDefinitionId: activityDefinitionId });

  const [assessmentUrl, setAssessmentUrl] = useState<string>();

  async function findAssessmentTask(subjectId: string, activityDefinitionId: string) {
    const statusQuery = INTERVIEW_ACTIVE_STATUSES.join(',');
    let query = `/Task?code=assessment&subject=${subjectId}&status=${statusQuery}`;
    let taskQueryRes;

    try {
      taskQueryRes = await fhirClient.search(query);
    } catch (error) {
      console.error(error);
      throw new Error('failed to get Task data to get assessment');
    }

    if (!taskQueryRes.entry || taskQueryRes.entry.length < 1) {
      return undefined;
    }

    const expectedInstantiatesCanonical = 'ActivityDefinition/' + activityDefinitionId;

    const assessmentTasks = taskQueryRes.entry
      .map(({ resource }) => resource as Task)
      .filter(
        (task) =>
          task &&
          task.instantiatesCanonical === expectedInstantiatesCanonical &&
          task.code?.coding?.some((coding) => coding?.code?.includes?.('assessment'))
      );

    if (!assessmentTasks.length) {
      return undefined;
    }

    if (metadata?.filterLast) {
      return assessmentTasks.reduce((latest, current) =>
        new Date(current?.lastModified || current.meta?.lastUpdated || 0).getTime() >
        new Date(latest?.lastModified || latest.meta?.lastUpdated || 0).getTime()
          ? current
          : latest
      );
    }

    return TaskWrapper(assessmentTasks[0]);
  }

  async function setAssessmentUrlFromTask(task: Task) {
    const inputAssessment = getAssessmentFromTask(task);

    if (!inputAssessment || !inputAssessment.valueUrl) {
      if (isRevisitTask) {
        try {
          const completedTasksResponse = (await fhirClient.search(
            `/Task?subject=${patientId}&code=assessment&status=completed&_count=10`
          )) as any;
          const allTasks =
            completedTasksResponse?.entry?.map((entry: any) => entry?.resource) || [];
          const initialTask = allTasks.find((t: Task) =>
            t?.instantiatesCanonical?.includes('INITIAL-SCREENING')
          );

          if (initialTask) {
            const initialInput = getAssessmentFromTask(initialTask);

            if (initialInput?.valueUrl) {
              const baseUrl = new URL(initialInput.valueUrl);

              baseUrl.searchParams.set('taskUUID', task.id || '');
              baseUrl.searchParams.set('patientUUID', patientId || '');
              baseUrl.searchParams.set('newtest', 'Y');
              baseUrl.searchParams.set('lang', 'en');

              const revisitAssessmentUrl = baseUrl.toString();

              const assessmentUrlWithUserId = getAssessmentUrlWithUserId(
                { valueUrl: revisitAssessmentUrl } as any,
                authUser.user_fhir_uri
              );

              standardLoadingBackdrop.close();
              setAssessmentUrl(assessmentUrlWithUserId);
              return;
            }
          }
        } catch (error) {
          console.error('Failed to generate revisit assessment URL:', error);
        }
      }

      enqueueSnackbar(translate('notReadyContactAdminMessage'), {
        variant: 'error',
      });
      handleClose(false);
      return;
    }

    if (!inputAssessment.valueUrl.includes('token=')) {
      enqueueSnackbar(metadata?.errorMessage, {
        variant: 'error',
      });
      handleClose(false);
      return;
    }

    const assessmentUrlWithUserId = getAssessmentUrlWithUserId(
      inputAssessment,
      authUser.user_fhir_uri,
      { taskId: task?.id, patientId: patient?.id }
    );

    standardLoadingBackdrop.close();

    setAssessmentUrl(assessmentUrlWithUserId);
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms || 300));
  }

  async function createTask(activityDefinitionId: string) {
    const response = await taskLinker.createAssessmentTask(
      activityDefinitionId,
      null,
      basedOn,
      metadata?.allowMany
    );

    if (response?.create === false) {
      enqueueSnackbar(translate(`errors.${response?.data?.errorId}`), {
        variant: 'error',
      });
      handleClose(false);
      throw new Error('failed to get Assessment Task data');
    }

    enqueueSnackbar(translate('preparingWaitMessage'), {
      variant: 'info',
    });

    await sleep(12000);

    const assessmentTask = await findAssessmentTask(patientId, activityDefinitionId);

    return assessmentTask ? TaskWrapper(assessmentTask) : undefined;
  }

  async function initialHandler(patientId: string, activityDefinitionId: string) {
    const getInitialTask = async () => {
      if (isRevisitTask && revisitValidation.latestTask) {
        return revisitValidation.latestTask;
      }

      return findAssessmentTask(patientId, activityDefinitionId);
    };

    const isInactiveTask = (task?: Task | WrappedTask) =>
      Boolean(task?.status && !TaskWrapper(task).isActive());

    let assessmentTask = await getInitialTask();

    if (displayStartRelatedAssessmentFirst && !assessmentTask) {
      enqueueSnackbar(translate('startRelatedAssessmentFirst'), {
        variant: 'error',
      });
      handleClose(false);
      return;
    }

    const canCreateTask = !assessmentTask && !isRevisitTask && metadata?.createTask !== false;
    if (canCreateTask) {
      assessmentTask = await createTask(activityDefinitionId);
    }

    if (!assessmentTask) {
      enqueueSnackbar(translate('notReadyContactAdminMessage'), {
        variant: 'error',
      });
      handleClose(false);
      return;
    }

    if (isInactiveTask(assessmentTask) && metadata?.allowMany) {
      assessmentTask = await createTask(activityDefinitionId);
    }

    if (isInactiveTask(assessmentTask)) {
      enqueueSnackbar(translate('doneMessage'), { variant: 'info' });
      handleClose(true, assessmentTask);
      return;
    }

    setAssessmentUrlFromTask(assessmentTask);
  }

  async function verifyHandler(
    patientId: string,
    activityDefinitionId: string,
    task?: WrappedTask
  ) {
    let assessmentTask: any = task;

    if (!assessmentTask) {
      assessmentTask = await findAssessmentTask(patientId, activityDefinitionId);
    }

    const taskWrapper = TaskWrapper(assessmentTask);

    if (assessmentTask.status && !taskWrapper.isActive()) {
      handlerSave({
        fhirReference: mapFhirResourceReference(assessmentTask),
        instantiatesCanonical: assessmentTask.instantiatesCanonical,
      });
    }
  }

  async function handleClose(verify: boolean = true, task?: WrappedTask) {
    if (patientId && activityDefinitionId && verify) {
      await verifyHandler(patientId, activityDefinitionId, task);
    }
    standardLoadingBackdrop.close();
    setAssessmentUrl(undefined);
    onClose();
  }

  useEffect(() => {
    if (open) {
      standardLoadingBackdrop.open();
    }

    if (isRevisitTask && revisitValidation.isLoading) {
      return;
    }

    if (isRevisitTask && open && !revisitValidation.latestTask) {
      enqueueSnackbar(
        metadata?.errorMessage ||
          'The screening has not been completed or there are no unanswered questions',
        { variant: 'error' }
      );
      handleClose(false);
      return;
    }

    if (open && patientId && activityDefinitionId) {
      initialHandler(patientId, activityDefinitionId);
    }
  }, [open, patientId, activityDefinitionId, isRevisitTask, revisitValidation.isLoading]);

  useEffect(() => {
    if (standardLoadingBackdrop.forceClose) {
      handleClose(false);
    }
  }, [standardLoadingBackdrop.forceClose]);

  return assessmentUrl ? (
    <AssessmentFrame open={open} url={assessmentUrl} onCancel={handleClose} />
  ) : null;
}

export function AssessmentChecklistDataButton({
  followUpHandler,
  i18n,
}: {
  followUpHandler: ({ checklistItem }: any) => void;
  i18n: i18nFunction;
}) {
  return (
    <Grid item xs={3} display={'flex'} justifyContent={'flex-end'}>
      <Button
        sx={{
          height: '100%',
          minWidth: '64px',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
        disabled={false}
        onClick={followUpHandler}
        variant="contained"
        startIcon={<Iconify icon={'eva:plus-fill'} />}
        data-name={'add-button'}
      >
        {i18n('', 'crs')}
        New Re-Screening
      </Button>
    </Grid>
  );
}
